import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, TrendingUp } from 'lucide-react';
import { suiteAPI } from '../lib/api';

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function ClientReportPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    suiteAPI.sharedReport(token)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.status === 410 ? 'This report link has expired.' : 'Report not found.'));
  }, [token]);

  if (error) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-8 text-center">
      <div><h1 className="text-2xl font-bold text-white mb-2">Report unavailable</h1><p className="text-zinc-500">{error}</p></div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="animate-spin text-[#b8ff00]" /></div>;

  const t = data.totals || {};
  const accent = data.brand?.primary_color || '#b8ff00';

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="client-report">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <p className="text-[11px] uppercase tracking-[0.2em]" style={{ color: accent }}>{data.brand?.brand_name || 'Marketing Report'}</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-2">{data.title}</h1>
        <p className="text-sm text-zinc-500 mt-2">Generated {data.generated_at?.slice(0, 16).replace('T', ' ')} UTC</p>
        {data.note && <p className="text-sm text-zinc-300 mt-6 max-w-2xl leading-relaxed">{data.note}</p>}

        {data.campaign && (
          <div className="mt-10 p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
            <h2 className="text-base md:text-lg font-semibold">{data.campaign.name}</h2>
            <p className="text-xs text-zinc-500 capitalize mt-1">{data.campaign.objective} · {data.campaign.status} · {data.campaign.start_date || '—'} → {data.campaign.end_date || 'open'}</p>
            {data.key_message && <p className="text-sm mt-4" style={{ color: accent }}>{data.key_message}</p>}
            {data.strategy && <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{data.strategy}</p>}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
          {[
            ['Spend', money(t.spend)], ['Revenue', money(t.revenue)], ['ROI', `${t.roi}%`], ['ROAS', `${t.roas}x`],
            ['Clicks & scans', Number(t.total_clicks || 0).toLocaleString()], ['Page views', Number(t.page_views || 0).toLocaleString()],
            ['Leads', t.leads], ['Cost per lead', money(t.cpl)],
          ].map(([l, v]) => (
            <div key={l} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500">{l}</p>
              <p className="text-2xl font-bold mt-1">{v}</p>
            </div>
          ))}
        </div>

        {data.funnel && (
          <div className="mt-8 p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
            <h3 className="text-sm font-bold mb-4 flex items-center gap-2"><TrendingUp size={14} style={{ color: accent }} /> Funnel</h3>
            {data.funnel.map(f => {
              const max = Math.max(...data.funnel.map(x => x.value), 1);
              return (
                <div key={f.stage} className="mb-3">
                  <div className="flex justify-between text-xs mb-1"><span className="text-zinc-400">{f.stage}</span><span>{Number(f.value).toLocaleString()}</span></div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full" style={{ width: `${(f.value / max) * 100}%`, background: accent }} /></div>
                </div>
              );
            })}
          </div>
        )}

        {data.deliverables && (
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mt-8">
            {Object.entries(data.deliverables).map(([k, v]) => (
              <div key={k} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
                <p className="text-lg font-bold">{v}</p>
                <p className="text-[10px] text-zinc-500 capitalize">{k.replace(/_/g, ' ')}</p>
              </div>
            ))}
            {data.tasks && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
                <p className="text-lg font-bold">{data.tasks.done}/{data.tasks.total}</p>
                <p className="text-[10px] text-zinc-500">tasks done</p>
              </div>
            )}
          </div>
        )}

        {data.campaigns && (
          <div className="mt-8 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-zinc-500 text-left text-xs">
                {['Campaign', 'Status', 'Spend', 'Revenue', 'Leads', 'ROI'].map(h => <th key={h} className="py-2 pr-4 font-medium">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.campaigns.map((c, i) => (
                  <tr key={i} className="border-t border-zinc-800">
                    <td className="py-2 pr-4">{c.name}</td>
                    <td className="py-2 pr-4 text-zinc-400 capitalize">{c.status}</td>
                    <td className="py-2 pr-4">{money(c.spend)}</td>
                    <td className="py-2 pr-4" style={{ color: accent }}>{money(c.revenue)}</td>
                    <td className="py-2 pr-4">{c.leads}</td>
                    <td className="py-2 pr-4">{c.roi}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-[11px] text-zinc-600 mt-14">Read-only report. Figures update automatically each time this page is opened.</p>
      </div>
    </div>
  );
}
